export interface PlantItem {
  image: string;
  name: string;
  type: string;
  tags: string[];
  colorTheme: string;
  description: string;
  care: string[];
}

export class PopularPlantsCarousel {
  private container: HTMLElement;
  private track: HTMLElement;
  private plants: PlantItem[];
  private cards: HTMLElement[] = [];
  private currentIndex: number = 0;
  private rotationAngle: number = 0;
  private autoScrollInterval: number | null = null;
  private isPaused: boolean = false;
  private autoScrollSpeed: number = 2500; // milliseconds per card (2.5 seconds)
  private radius: number = 500; // Radius of the circular carousel
  private animationFrame: number | null = null;
  private isAnimating: boolean = false;
  private animationDelay: number = 200; // Delay between each card animation in sequence

  constructor(container: HTMLElement) {
    this.container = container;
    this.track = container.querySelector('.carousel-3d-track') as HTMLElement;
    
    // Plant data (images from public folder)
    this.plants = [
      {
        image: '/Monstera_Deliciosa.png',
        name: 'Monstera Deliciosa',
        type: 'Tropical Plant',
        tags: ['Indoor', 'Low Light'],
        colorTheme: 'green',
        description: 'A striking tropical plant with large, glossy leaves that develop characteristic splits and holes as they mature. Native to Central America and ideal for bright indoor spaces.',
        care: [
          'Bright, indirect light; avoid direct midday sun.',
          'Water when the top 2–3 inches of soil are dry; enjoys humidity.',
          'Use well-draining potting mix and a support pole as it grows.',
        ],
      },
      {
        image: '/Snake_Plant.png',
        name: 'Snake Plant',
        type: 'Succulent',
        tags: ['Indoor', 'Low Maintenance'],
        colorTheme: 'purple',
        description: 'A hardy succulent with upright, sword-like leaves. Tolerates low light and infrequent watering, making it one of the easiest houseplants to care for.',
        care: [
          'Low to bright indirect light; very adaptable.',
          'Water sparingly; allow soil to dry fully between waterings.',
          'Avoid overwatering; susceptible to root rot in wet soil.',
        ],
      },
      {
        image: '/Fiddle_Leaf_Fig.png',
        name: 'Fiddle Leaf Fig',
        type: 'Tree',
        tags: ['Indoor', 'Bright Light'],
        colorTheme: 'blue',
        description: 'A statement tree with large, violin-shaped leaves. Prefers consistent conditions and bright indirect light to thrive indoors.',
        care: [
          'Bright indirect light; avoid drafts and sudden moves.',
          'Water when topsoil is dry; wipe leaves occasionally for dust.',
          'Prefers stable temperature and moderate humidity.',
        ],
      },
      {
        image: '/Golden_Pothos.png',
        name: 'Golden Pothos',
        type: 'Vine',
        tags: ['Indoor', 'Easy Care'],
        colorTheme: 'yellow',
        description: 'A fast-growing trailing vine with heart-shaped, variegated leaves. Very forgiving and great for beginners or hanging baskets.',
        care: [
          'Low to bright indirect light; tolerates lower light well.',
          'Water when soil feels dry; can tolerate occasional neglect.',
          'Trim long vines to encourage bushier growth; easy to propagate.',
        ],
      },
      {
        image: '/Peace_Lily.png',
        name: 'Peace Lily',
        type: 'Flowering Plant',
        tags: ['Indoor', 'Low Light'],
        colorTheme: 'cyan',
        description: 'An elegant plant with dark green leaves and white spathe flowers. Tolerates low light and will droop slightly when it needs water.',
        care: [
          'Low to medium indirect light; avoid direct sun.',
          'Keep soil lightly moist; water when leaves droop or soil is dry.',
          'Enjoys humidity; brown leaf tips may indicate dry air.',
        ],
      },
      {
        image: '/ZZ_Plant.png',
        name: 'ZZ Plant',
        type: 'Succulent',
        tags: ['Indoor', 'Drought Tolerant'],
        colorTheme: 'orange',
        description: 'A tough plant with glossy, waxy leaves on arching stems. Tolerates low light and long periods without water thanks to rhizome storage.',
        care: [
          'Low to bright indirect light; very low-light tolerant.',
          'Water infrequently; allow soil to dry completely between waterings.',
          'Drought-tolerant; overwatering is the main cause of problems.',
        ],
      },
    ];

    this.init();
  }

  private init(): void {
    this.createCards();
    this.setupAutoScroll();
    this.setupInteractions();
    this.setupNavButtons();
    this.updateCardPositions();
    this.animateCardsSequentially();
  }

  private setupNavButtons(): void {
    const wrap = this.container.closest('.popular-plants-carousel-wrap');
    if (!wrap) return;
    const prevBtn = wrap.querySelector('.popular-plants-nav--prev') as HTMLButtonElement | null;
    const nextBtn = wrap.querySelector('.popular-plants-nav--next') as HTMLButtonElement | null;
    if (prevBtn) prevBtn.addEventListener('click', () => this.rotateToPrev());
    if (nextBtn) nextBtn.addEventListener('click', () => this.rotateToNext());
  }

  private rotateToPrev(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.currentIndex = (this.currentIndex - 1 + this.plants.length) % this.plants.length;
    const targetAngle = (360 / this.plants.length) * this.currentIndex;
    const startAngle = this.rotationAngle;
    let angleDiff = ((targetAngle - startAngle + 180) % 360) - 180;
    if (angleDiff > 0) angleDiff -= 360;
    const duration = 1200;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      this.rotationAngle = startAngle + angleDiff * easeProgress;
      this.updateCardPositions();
      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.isAnimating = false;
        this.animationFrame = null;
      }
    };
    this.animationFrame = requestAnimationFrame(animate);
  }

  private createCards(): void {
    if (!this.track) return;

    // Clear existing cards
    this.track.innerHTML = '';
    this.cards = [];

    // Create cards for each plant
    this.plants.forEach((plant, index) => {
      const card = document.createElement('div');
      card.className = 'holographic-card';
      card.setAttribute('data-index', index.toString());
      card.setAttribute('data-color', plant.colorTheme);
      
      const tagsHtml = plant.tags.map(tag => `<span class="plant-tag">${tag}</span>`).join('');
      
      card.innerHTML = `
        <div class="card-holographic-overlay"></div>
        <div class="plant-card-background-image" style="background-image: url('${plant.image}')"></div>
        <div class="plant-card-gradient-overlay"></div>
        <div class="card-content">
          <h3 class="card-title">${plant.name}</h3>
          <p class="card-description">${plant.type}</p>
          <div class="plant-tags">${tagsHtml}</div>
        </div>
      `;
      
      this.track.appendChild(card);
      this.cards.push(card);
    });
  }

  private setupAutoScroll(): void {
    this.autoScrollInterval = window.setInterval(() => {
      if (!this.isPaused && !this.isAnimating) {
        this.rotateToNext();
      }
    }, this.autoScrollSpeed);
  }

  private rotateToNext(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.currentIndex = (this.currentIndex + 1) % this.plants.length;
    const targetAngle = (360 / this.plants.length) * this.currentIndex;
    const startAngle = this.rotationAngle;
    const angleDiff = ((targetAngle - startAngle + 180) % 360) - 180;
    const duration = 1200;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-in-out)
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      this.rotationAngle = startAngle + angleDiff * easeProgress;
      this.updateCardPositions();

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.isAnimating = false;
        this.animationFrame = null;
      }
    };
    
    this.animationFrame = requestAnimationFrame(animate);
  }

  private updateCardPositions(): void {
    this.cards.forEach((card, index) => {
      const anglePerCard = 360 / this.plants.length;
      const cardAngle = (index * anglePerCard + this.rotationAngle) * (Math.PI / 180);
      
      const x = Math.sin(cardAngle) * this.radius;
      const z = Math.cos(cardAngle) * this.radius;
      const distanceFromViewer = z;
      const normalizedDistance = (distanceFromViewer + this.radius) / (2 * this.radius);
      const scale = 0.6 + normalizedDistance * 0.4;
      const clampedScale = Math.max(0.6, Math.min(1, scale));
      const opacity = 0.4 + normalizedDistance * 0.6;
      const clampedOpacity = Math.max(0.4, Math.min(1, opacity));
      const cardRotation = -cardAngle * (180 / Math.PI);
      
      // Calculate new values - use translate3d for hardware acceleration
      // Round values to reduce micro-updates that cause flickering
      const roundedX = Math.round(x * 100) / 100;
      const roundedZ = Math.round(z * 100) / 100;
      const roundedRotation = Math.round(cardRotation * 100) / 100;
      const roundedScale = Math.round(clampedScale * 1000) / 1000;
      const roundedOpacity = Math.round(clampedOpacity * 1000) / 1000;
      
      // Format with consistent spacing to ensure string comparison works
      const newTransform = `translate3d(${roundedX}px, 0px, ${roundedZ}px) rotateY(${roundedRotation}deg) scale(${roundedScale})`;
      const newOpacity = roundedOpacity.toString();
      // Use discrete bands (0–10) to reduce z-index thrashing and flicker when cards have similar depth
      const zIndexBand = Math.min(10, Math.floor(normalizedDistance * 11));
      const newZIndexNum = zIndexBand * 10;
      const newZIndex = newZIndexNum.toString();

      // Only update styles if values actually changed to prevent unnecessary repaints
      const currentTransform = (card.style.transform || '').replace(/\s+/g, ' ').trim();
      const normalizedNewTransform = newTransform.replace(/\s+/g, ' ').trim();
      
      if (currentTransform !== normalizedNewTransform) {
        card.style.transform = newTransform;
      }

      if (card.style.opacity !== newOpacity) {
        card.style.opacity = newOpacity;
      }
      
      const currentZIndex = parseInt(card.style.zIndex || '0', 10);
      if (Math.abs(currentZIndex - newZIndexNum) >= 10) {
        card.style.zIndex = newZIndex;
      }
    });
  }

  private animateCardsSequentially(): void {
    // Reset all cards to initial state
    this.cards.forEach((card) => {
      card.style.opacity = '0';
      card.style.transform = 'scale(0.8) translateY(50px)';
      card.style.transition = 'none';
    });

    this.cards.forEach((card, index) => {
      setTimeout(() => {
        card.style.transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
        this.updateCardPositions();
        
        // Trigger reflow to ensure transition works
        void card.offsetWidth;
        
        // Apply final position with animation
        const cardIndex = index % this.plants.length;
        const anglePerCard = 360 / this.plants.length;
        const cardAngle = (cardIndex * anglePerCard + this.rotationAngle) * (Math.PI / 180);
        
        const x = Math.sin(cardAngle) * this.radius;
        const z = Math.cos(cardAngle) * this.radius;
        const distanceFromViewer = z;
        const normalizedDistance = (distanceFromViewer + this.radius) / (2 * this.radius);
        const scale = 0.6 + normalizedDistance * 0.4;
        const clampedScale = Math.max(0.6, Math.min(1, scale));
        const opacity = 0.4 + normalizedDistance * 0.6;
        const clampedOpacity = Math.max(0.4, Math.min(1, opacity));
        const cardRotation = -cardAngle * (180 / Math.PI);
        
        card.style.transform = `
          translateX(${x}px)
          translateZ(${z}px)
          rotateY(${cardRotation}deg)
          scale(${clampedScale})
        `;
        card.style.opacity = clampedOpacity.toString();
      }, index * this.animationDelay);
    });
  }

  private setupInteractions(): void {
    // Pause on hover
    this.container.addEventListener('mouseenter', () => {
      this.isPaused = true;
    });

    this.container.addEventListener('mouseleave', () => {
      this.isPaused = false;
    });

    // Click to open plant details dialog
    this.cards.forEach((card, index) => {
      card.style.cursor = 'pointer';
      card.addEventListener('click', () => {
        const plant = this.plants[index];
        const payload = {
          title: plant.name,
          subtitle: plant.type,
          description: plant.description,
          care: plant.care,
          imageUrl: plant.image,
        };
        window.dispatchEvent(
          new CustomEvent('plant-details-request', { detail: payload })
        );
      });
    });
  }

  /**
   * Pause the carousel animation
   */
  public pause(): void {
    this.isPaused = true;
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Resume the carousel animation
   */
  public resume(): void {
    this.isPaused = false;
  }

  public dispose(): void {
    if (this.autoScrollInterval !== null) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
    
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }
}
